import React from 'react';
import PanelBreadcrumbs from './PanelBreadcrumbs';

const FeatureCard = ({ icon, title, description, bullets }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center text-xl">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
          {bullets?.length ? (
            <ul className="mt-3 space-y-1 text-sm text-gray-700">
              {bullets.map((b, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-indigo-600 mt-0.5">•</span>
                  <span className="min-w-0">{b}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const AiAnalyticsAssistant = ({ setActivePage }) => {
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <PanelBreadcrumbs current="AI Analytics Assistant" setActivePage={setActivePage} />
        {/* HERO */}
        <section className="bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-600 rounded-3xl text-white p-8 md:p-12 shadow-sm overflow-hidden relative">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-white rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-white rounded-full blur-3xl" />
          </div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-3 py-1 text-sm">
              <span>🧠</span>
              <span className="font-medium">AI Analytics Assistant</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mt-4 leading-tight">
              Ask Questions. Get Insights. Instantly.
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-4 max-w-2xl">
              Turn your data into answers, charts, and reports — without writing SQL or building dashboards.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setActivePage?.('chat')}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors"
              >
                Start Asking Questions
                <span aria-hidden>→</span>
              </button>
              <button
                onClick={() => setActivePage?.('pinned-reports')}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition-colors"
              >
                View Pinned Reports
              </button>
            </div>
          </div>
        </section>

        {/* FEATURE GRID */}
        <section className="mt-10">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">✨ What You Can Do</h2>
              <p className="text-gray-600 mt-1">Everything you need to go from question to action.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon="💬"
              title="Natural Language Queries"
              description="Ask anything about your data"
              bullets={['“Top products last month”', '“Revenue trend this quarter”', '→ Get answers in seconds']}
            />
            <FeatureCard
              icon="📊"
              title="Auto-Generated Visuals"
              description="Smart charts created instantly"
              bullets={['Bar · Line · Pie · Multi-line · Dual-axis', '→ No manual setup needed']}
            />
            <FeatureCard
              icon="📌"
              title="Save & Reuse Reports"
              description="Pin important insights"
              bullets={['Re-run anytime with latest data', '→ Build your own live dashboards']}
            />
            <FeatureCard
              icon="📥"
              title="Export Ready Data"
              description="Download results as CSV"
              bullets={['Use for reporting or sharing', '→ Take action faster']}
            />
            <FeatureCard
              icon="🧠"
              title="Intelligent by Default"
              description="No guesswork required"
              bullets={['Auto-applies time ranges', 'Smart limits & aggregations', 'Clean, structured output']}
            />
            <FeatureCard
              icon="⚡"
              title="Why Teams Love It"
              bullets={[
                '⚡ Get answers instantly — no waiting',
                '👥 No dependency on developers or analysts',
                '📈 Make faster, data-driven decisions',
              ]}
            />
          </div>
        </section>

        {/* WHY SECTION */}
        <section className="mt-12">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 md:p-10">
            <h2 className="text-2xl font-bold text-gray-900">🎯 From Question → Insight</h2>
            <p className="text-gray-600 mt-2 max-w-3xl">
              You ask: <span className="font-medium text-gray-900">“Compare sales across categories in last 3 months”</span>
            </p>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* EXAMPLE BLOCK */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <div className="text-sm text-gray-500">You ask</div>
                <div className="mt-2 font-semibold text-gray-900">
                  “Compare sales across categories in last 3 months”
                </div>
                <div className="mt-5 text-sm text-gray-500">You get</div>
                <ul className="mt-2 space-y-2 text-sm text-gray-800">
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✔</span>
                    <span>Clean dataset</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✔</span>
                    <span>Interactive chart</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✔</span>
                    <span>Downloadable report</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✔</span>
                    <span>Saved (pinned) insight</span>
                  </li>
                </ul>
              </div>

              {/* OUTPUT PREVIEW */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500">Output preview</div>
                    <div className="text-base font-semibold text-gray-900 mt-1">Sales by Category (Last 3 Months)</div>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                      Chart
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                      CSV
                    </span>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    { label: 'Electronics', value: '$182,410' },
                    { label: 'Apparel', value: '$121,930' },
                    { label: 'Home', value: '$94,220' },
                    { label: 'Beauty', value: '$63,800' },
                  ].map((row) => (
                    <div key={row.label} className="rounded-xl border border-gray-200 p-4">
                      <div className="text-sm text-gray-600">{row.label}</div>
                      <div className="text-lg font-bold text-gray-900 mt-1">{row.value}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 h-28 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 flex items-end justify-between px-4 pb-4">
                  {[28, 55, 42, 76, 60, 90].map((h, idx) => (
                    <div key={idx} className="w-6 rounded-md bg-indigo-500/70" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-10 mb-2">
          <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🚀 Start Exploring Your Data</h2>
              <p className="text-gray-600 mt-2">
                Ask your first question and unlock insights in seconds.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setActivePage?.('chat')}
                className="px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
              >
                Start Asking Questions
              </button>
              <button
                onClick={() => setActivePage?.('dashboard')}
                className="px-5 py-3 rounded-xl bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AiAnalyticsAssistant;

